/**
 * Partner › Overview (TMMPDM-0088): Needs you, then At a glance. Needs you lists the four standing
 * alerts (`ResellerOverviewPanel`), then items 5–8 in order, each driven by one server field and
 * each opening the leaf that fixes it:
 *   5. no tenant yet — `reseller.tenant === null`;
 *   6. can't sell — the tenant read's `sellableReason` (the existing alert, unchanged);
 *   7. official launch not set — the standing's `officialLaunchOn === null`, once it has loaded;
 *   8. charges can be retried — the count of charges whose own `retryable` is true (one read here);
 * Item 9 is not built: tmm-api 0154 retired its flag (Operator, 2026-10-07).
 * At a glance is read-only: the platform-managed note, the cards and the tenant's figures.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Alert, AlertTitle, Button } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { adminApi } from '@/api/tmm/admin';
import type {
  FleetDashboard,
  ResellerDto,
  ResellerOutOfBand,
  ResellerStandingState,
  ResellerStandingView,
} from '@/api/tmm/types';
import { ResellerOverviewPanel } from '../resellers/ResellerOverviewPanel';
import { NotSellableAlert } from '../tenants/Sellable';
import { PartnerOverviewCards } from './PartnerOverviewCards';
import { NoTenantNotice, PartnerTenantPanel, type usePartnerTenant } from './PartnerTenantPanel';
import type { TabKey } from './partnerSections';

export interface PartnerOverviewProps {
  reseller: ResellerDto;
  displayReseller: ResellerDto;
  standing: ResellerStandingView | null;
  standingState: ResellerStandingState | null;
  standingLoading: boolean;
  standingFailure: unknown;
  onStandingRetry: () => void;
  outOfBand: ResellerOutOfBand | null;
  onReload: () => void;
  focusHeading: () => void;
  onOpenDeactivate: () => void;
  actionsBusy: boolean;
  tenantDashboard: (slug: string, query: { from: string; to: string }) => Promise<FleetDashboard>;
  tenantLoad: ReturnType<typeof usePartnerTenant>;
  tenantEditOpen: boolean;
  onTenantEditOpenChange: (open: boolean) => void;
  onCreateTenant: () => void;
  /** Opens a leaf as a leaf click does (a history entry, the page's resets). */
  openTab: (tab: TabKey) => void;
}

/** Item 8's read: the partner's charges, once per Overview visit. */
function useRetryableCharges(resellerId: number) {
  const [token, setToken] = useState(0);
  const [state, setState] = useState<{
    token: number;
    retryable: number | null;
    failure: unknown;
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    adminApi
      .listResellerCharges(resellerId)
      .then((list) => {
        if (!cancelled)
          setState({
            token,
            retryable: list.charges.filter((charge) => charge.retryable).length,
            failure: null,
          });
      })
      .catch((failure: unknown) => {
        if (!cancelled) setState({ token, retryable: null, failure: failure ?? 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [resellerId, token]);
  const current = state?.token === token ? state : null;
  return {
    loading: current === null,
    retryable: current?.retryable ?? null,
    failed: current !== null && current.failure !== null,
    retry: () => setToken((n) => n + 1),
  };
}

export function PartnerOverview({
  reseller,
  displayReseller,
  standing,
  standingState,
  standingLoading,
  standingFailure,
  onStandingRetry,
  outOfBand,
  onReload,
  focusHeading,
  onOpenDeactivate,
  actionsBusy,
  tenantDashboard,
  tenantLoad,
  tenantEditOpen,
  onTenantEditOpenChange,
  onCreateTenant,
  openTab,
}: PartnerOverviewProps) {
  const { t } = useCopy();
  const charges = useRetryableCharges(reseller.id);
  const tenant = tenantLoad.tenant;

  const items: ReactNode[] = [];
  // 5. No tenant yet.
  if (reseller.tenant === null) {
    items.push(<NoTenantNotice key="noTenant" reseller={reseller} onCreate={onCreateTenant} />);
  }
  // 6. Can't sell — the existing alert, from the tenant read's `sellableReason`.
  if (tenant !== null && tenant.sellableReason !== null) {
    items.push(
      <NotSellableAlert
        key="sellable"
        tenant={tenant}
        resellerName={
          tenant.reseller?.name ?? (tenant.reseller === null ? '' : `#${tenant.reseller.id}`)
        }
        onEditTenant={() => onTenantEditOpenChange(true)}
      />,
    );
  }
  // 7. Official launch not set — once the standing has loaded.
  if (standing !== null && standing.officialLaunchOn === null) {
    items.push(
      <Alert
        key="launch"
        severity="info"
        action={
          <Button color="inherit" size="small" onClick={() => openTab('agreement')}>
            {t('partners.needsYou.openAgreement', 'Open agreement')}
          </Button>
        }
      >
        <AlertTitle>{t('partners.needsYou.launch.title', 'Official launch not set')}</AlertTitle>
        {t(
          'partners.needsYou.launch.body',
          'No month is checked against the minimum until a launch month is set.',
        )}
      </Alert>,
    );
  }
  // 8. Charges can be retried — each charge's own `retryable`.
  if (charges.failed) {
    items.push(
      <Alert
        key="chargesFailed"
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={charges.retry}>
            {t('common.retry', 'Retry')}
          </Button>
        }
      >
        {t('partners.needsYou.charges.loadError', "Couldn't check this partner's charges.")}
      </Alert>,
    );
  } else if (charges.retryable !== null && charges.retryable > 0) {
    items.push(
      <Alert
        key="charges"
        severity="warning"
        action={
          <Button color="inherit" size="small" onClick={() => openTab('billing')}>
            {t('partners.needsYou.openCharges', 'Open charges')}
          </Button>
        }
      >
        <AlertTitle>
          {t('partners.needsYou.charges.title', '{{count, number}} charges can be retried', {
            count: charges.retryable,
          })}
        </AlertTitle>
        {t(
          'partners.needsYou.charges.body',
          "A charge to this partner's card failed. You can try it again.",
        )}
      </Alert>,
    );
  }
  const pending =
    standingLoading || charges.loading || (reseller.tenant !== null && tenantLoad.loading);

  return (
    <ResellerOverviewPanel
      reseller={reseller}
      platformManagedSince={standing?.platformManagedSince ?? null}
      standingState={standingState}
      standingLoading={standingLoading}
      standingFailure={standingFailure}
      onStandingRetry={onStandingRetry}
      outOfBand={outOfBand}
      reactivateOffered={standing?.reactivateOffered ?? false}
      onReload={onReload}
      onFocusHeading={focusHeading}
      onOpenDeactivate={onOpenDeactivate}
      actionsBusy={actionsBusy}
      extraNeedsYou={items}
      needsYouPending={pending}
      cards={
        <PartnerOverviewCards
          reseller={displayReseller}
          tenantDashboard={tenantDashboard}
          standing={{
            state: standingState,
            loading: standingLoading,
            failure: standingFailure,
            onRetry: onStandingRetry,
          }}
        />
      }
      glance={
        <PartnerTenantPanel
          tab="overview"
          reseller={reseller}
          load={tenantLoad}
          tenantDashboard={tenantDashboard}
          onCreate={onCreateTenant}
          tenantEditOpen={tenantEditOpen}
          onTenantEditOpenChange={onTenantEditOpenChange}
          noTenantNotice={false}
          showSellable={false}
        />
      }
    />
  );
}
